import { ContentCopy, Info } from '@mui/icons-material';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { cellFormat } from 'lib/designSystem/Table/cellFormat';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { getDueWorkingDay, isAValidDate } from 'lib/functions/dates';
import {
  formatDate,
  formatMoney,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../../../../../services/redux/authSlice';

interface CustomTooltipRow {
  name: string;
  value: string;
  bold?: boolean;
}

const CustomTooltip = (tableData: CustomTooltipRow[], noHeader?: boolean) => (
  <Table size="small">
    {!noHeader && (
      <TableHead>
        <TableRow>
          <TableCell>Costo</TableCell>
          <TableCell>Valor</TableCell>
        </TableRow>
      </TableHead>
    )}
    <TableBody>
      {tableData.map((row: CustomTooltipRow) => (
        <TableRow key={row.name}>
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.value}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
export interface CollectionState {
  collectionManagementOptions: any[];
  companyId: number;
  createdAt: string;
  id: number;
  name: string;
  updatedAt: string;
}

export const OptionsGrid = (props: any) => {
  const {
    executiveId,
    documentHasExecutives,
    byStakeholder,
    documentsForMultiple,
    checkFields,
    option,
    setOption,
    setExecutiveId,
    loading,
    executivesOptions,
    portfolioExecutiveId,
    setPortfolioExecutiveId,
    stateId,
    handleSelectState,
    stateOptions,
    isManager,
    completedCheckbox,
    setCompletedCheckbox,
    completedDate,
    dueDate,
    description,
    setDescription,
    contactMethod,
    disabled,
    setDueDate,
    setCompletedDate,
    setContactMethod,
    options,
    possiblePaymentDate,
    setPossiblePaymentDate,
    managementsPossiblePaymentDate,
    setManagementsPossiblePaymentDate,
  } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();

  const setUpPortfolioExecutiveId = () => {
    if (documentHasExecutives) {
      if (!validatePermissions(user, 'have_subordinates')) {
        if (user) {
          setExecutiveId(user.id);
        }
      }
    }
  };

  const handleSetDate = (date: string) => {
    let dueDate = new Date(date);
    dueDate.setUTCHours(12, 0, 0, 0);
    if (!isAValidDate(dueDate)) {
      dueDate = getDueWorkingDay(dueDate);
      showAlert(
        dispatch,
        'warning',
        `Se seleccionó un día no hábil, la fecha de gestión será el día ${cellFormat(dueDate, 'date')}`,
      );
    }
    const dueDateFormatted = dayjs(dueDate).format('YYYY-MM-DD');
    setDueDate(dueDateFormatted);
  };

  useEffect(() => {
    setUpPortfolioExecutiveId();
  }, []);

  const handleCopyFirstDateToAll = () => {
    if (documentsForMultiple.length === 0) return;

    const firstDocument = documentsForMultiple[0];
    const firstManagement = managementsPossiblePaymentDate.find(
      (management: any) => management.documentId === firstDocument.id,
    );

    const firstDate =
      firstManagement?.possiblePaymentDate || possiblePaymentDate;

    if (firstDate && firstDate !== 'none') {
      const updatedManagements = managementsPossiblePaymentDate.map(
        (management: any) => ({
          ...management,
          possiblePaymentDate: firstDate,
        }),
      );
      setManagementsPossiblePaymentDate(updatedManagements);
      setPossiblePaymentDate(firstDate);
    } else {
      showAlert(
        dispatch,
        'warning',
        'No se ha seleccionado una fecha de posible pago a copiar',
      );
    }
  };

  return (
    <div>
      {executiveId == 0 ? (
        documentHasExecutives ? (
          <div>
            <Typography variant="body1">
              Asigna la gestión múltiple a un ejecutivo
            </Typography>
            <Grid container spacing={2} style={{ paddingTop: '12px' }}>
              <Grid item xs={12}>
                <TextField
                  error={checkFields && option === ''}
                  select
                  value={executiveId}
                  label="Ejecutivo gestión múltiple"
                  variant="outlined"
                  fullWidth
                  onChange={(e) =>
                    setExecutiveId(e.target.value as unknown as number)
                  }
                  disabled={loading}
                >
                  {executivesOptions.map((element: any) => {
                    return (
                      <MenuItem key={element.id} value={element.id}>
                        {`${element.name} (${element.email})`}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
          </div>
        ) : isManager ? (
          <div>
            <Typography variant="body1">
              {`No hay ejecutivos con este ${
                byStakeholder ? 'deudor' : 'cliente'
              } en su cartera. Puedes asignar un ejecutivo a estos documentos asociando ${
                byStakeholder
                  ? `${documentsForMultiple?.[0]?.stakeholderName}`
                  : `${documentsForMultiple?.[0]?.businessName}`
              } a su cartera.`}
            </Typography>
            <Grid container spacing={2} style={{ paddingTop: '12px' }}>
              <Grid item xs={12}>
                <TextField
                  error={checkFields && option === ''}
                  select
                  value={portfolioExecutiveId}
                  label="Ejecutivo a asignar"
                  variant="outlined"
                  fullWidth
                  onChange={(e) =>
                    setPortfolioExecutiveId(e.target.value as unknown as number)
                  }
                  disabled={loading}
                >
                  {executivesOptions.map((element: any) => {
                    return (
                      <MenuItem key={element.id} value={element.id}>
                        {`${element.name} (${element.email})`}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
          </div>
        ) : (
          <Typography variant="body1">
            {`No hay ejecutivos con este ${
              byStakeholder ? 'deudor' : 'cliente'
            } en su cartera. Solicita a un gerente que asigne un ejecutivo a ${
              byStakeholder
                ? `${documentsForMultiple?.[0]?.stakeholderName}`
                : `${documentsForMultiple?.[0]?.businessName}`
            }.`}
          </Typography>
        )
      ) : (
        <>
          <Grid container spacing={2} style={{ paddingTop: '12px' }}>
            <Grid item xs={6}>
              <TextField
                error={checkFields && option === ''}
                select
                value={executiveId}
                label="Ejecutivo gestión"
                variant="outlined"
                fullWidth
                onChange={(e) =>
                  setExecutiveId(e.target.value as unknown as number)
                }
                disabled={loading}
              >
                {executivesOptions.map((element: any) => {
                  return (
                    <MenuItem key={element.id} value={element.id}>
                      {`${element.name} (${element.email})`}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">
                  Método de contacto
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                  disabled={disabled}
                  label="Método de contacto"
                >
                  {[
                    'Llamada',
                    'Correo',
                    'Whatsapp',
                    'Visita a terreno',
                    'Portal',
                  ].map((element) => {
                    return (
                      <MenuItem key={element} value={element}>
                        {element}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                error={checkFields && option === ''}
                select
                value={stateId}
                label="Estado gestión"
                variant="outlined"
                fullWidth
                onChange={(e) =>
                  handleSelectState(e.target.value as unknown as number)
                }
                disabled={loading}
              >
                {stateOptions.map((element: CollectionState) => {
                  return (
                    <MenuItem key={element.id} value={element.id}>
                      {element.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                error={checkFields && option === ''}
                select
                value={option}
                label="Subestado de gestión"
                variant="outlined"
                fullWidth
                onChange={(e) => setOption(e.target.value)}
                disabled={loading || !stateId}
              >
                {options.map((element: any) => {
                  return (
                    <MenuItem key={element.id} value={element.id}>
                      {element.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="outlined-basic"
                label="Fecha gestión"
                variant="outlined"
                type="date"
                value={dueDate}
                fullWidth
                onChange={(e) => {
                  handleSetDate(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                error={checkFields && description === ''}
                id="outlined-basic"
                label="Observación"
                variant="outlined"
                fullWidth
                multiline
                disabled={disabled || !stateId || !option}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={completedCheckbox}
                    onChange={(e) => setCompletedCheckbox(e.target.checked)}
                    name="completed"
                    color="primary"
                  />
                }
                label="Esta tarea ya fue completada?"
              />
            </Grid>
            {completedCheckbox && (
              <Grid item xs={12}>
                <TextField
                  id="outlined-basic"
                  label="Fecha de completado"
                  variant="outlined"
                  type="date"
                  value={completedDate}
                  fullWidth
                  onChange={(e) => {
                    setCompletedDate(e.target.value);
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Divider />
          {documentsForMultiple.map((document: any, index: number) => {
            return (
              <Grid
                container
                spacing={2}
                key={document.id}
                style={{ paddingTop: '12px', minHeight: '64px' }}
              >
                <Grid item xs={6}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '64px',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '64px',
                      }}
                    >
                      <Typography variant="body1">
                        {`Documento folio: ${document.folio}`}
                      </Typography>
                      <LightTooltip
                        title={CustomTooltip(
                          [
                            {
                              name: 'Folio',
                              value: document.folio,
                            },
                            {
                              name: 'Cliente',
                              value: document.businessName,
                            },
                            {
                              name: 'Deudor',
                              value: document.stakeholderName,
                            },
                            {
                              name: 'Monto',
                              value: formatMoney(document.amount),
                            },
                            {
                              name: 'Fecha de vencimiento',
                              value: formatDate(document.paymentDate),
                            },
                          ],
                          true,
                        )}
                      >
                        <Info style={{ marginLeft: 6, fontSize: 14 }} />
                      </LightTooltip>
                    </div>
                    {index === 0 && documentsForMultiple.length > 1 && (
                      <IconButton
                        onClick={handleCopyFirstDateToAll}
                        color="primary"
                      >
                        <ContentCopy />
                      </IconButton>
                    )}
                  </div>
                </Grid>
                <Grid item xs={6} key={document.id}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '64px',
                    }}
                  >
                    <TextField
                      id="outlined-basic-two"
                      label="Posible fecha de pago"
                      variant="outlined"
                      type="date"
                      value={
                        managementsPossiblePaymentDate.find(
                          (management: any) =>
                            management.documentId === document.id,
                        )?.possiblePaymentDate || possiblePaymentDate
                      }
                      fullWidth
                      onChange={(e) => {
                        setManagementsPossiblePaymentDate(
                          managementsPossiblePaymentDate.map(
                            (management: any) => {
                              if (management.documentId === document.id) {
                                return {
                                  ...management,
                                  possiblePaymentDate: e.target.value,
                                };
                              }
                              return management;
                            },
                          ),
                        );
                        setPossiblePaymentDate(e.target.value);
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            );
          })}
        </>
      )}
    </div>
  );
};

export default OptionsGrid;
